import {
  ArrowLeftOutlined,
  CalculatorOutlined,
  CheckOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  ReloadOutlined,
  RollbackOutlined,
  SaveOutlined,
  SwapOutlined,
  UndoOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App,
  Button,
  Card,
  Descriptions,
  Dropdown,
  Grid,
  InputNumber,
  Popconfirm,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import dayjs from 'dayjs';
import { useEffect, useRef, useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router';
import { apiErrorMessage } from '../api/client';
import { shipmentsApi } from '../api/endpoints';
import { invalidateMoneyData, queryKeys } from '../api/queryKeys';
import type { Shipment, ShipmentItem } from '../api/types';
import { useAuth } from '../auth/AuthContext';
import { useStickyTable } from '../context/StickyTableContext';
import { AmountInput } from '../components/AmountInput';
import { ErrorBlock, LoadingBlock } from '../components/PageStates';
import { ActorLine } from '../components/ActorLine';
import { ShipmentStatusTag } from '../components/ShipmentStatusTag';
import { useDownloadShipmentPdf } from '../hooks/useDownloadShipmentPdf';
import { UnsavedChangesGuard } from '../hooks/useUnsavedChangesPrompt';
import { ManualShipmentEditor } from './ManualShipmentEditor';
import { MoveItemModal } from './shipment/MoveItemModal';
import {
  ExtraSkladItems,
  extraGroupsComplete,
  extraGroupsFromItems,
  extraGroupsToItems,
  extraSideTotal,
  type ExtraGroup,
} from './shipment/ExtraSkladItems';
import { OWED_TO_OWNER, OWNER_OWES } from '../lib/balanceColors';
import { formatKopeks, isValidRubInput, kopeksToRubString, rubToKopeks } from '../lib/money';
import { formatDeltaPct, priceChanged } from '../lib/priceDelta';
import { mergeDraft, type PriceDraft } from '../lib/priceDraft';
import { normalizeName } from '../lib/productName';
import { returnToOf } from '../lib/returnTo';
import { formatQuantityMilli } from '../lib/quantity';

/** Mirrors backend shipment-math.lineSum — the two MUST round identically. */
function lineSum(quantityMilli: number, priceKopeks: number): number {
  return Math.round((quantityMilli * priceKopeks) / 1000);
}

/** A posted накладная's lines grouped by the sklad they post under, so a
 * multi-sklad document reads the same way it was created (one section per sklad). */
interface SkladGroup {
  key: string;
  /** Sklad name; empty for the MoySklad (main) group, flagged by `isMain`. */
  name: string;
  /** The demand's own MoySklad lines — titled «МойСклад», not «Склад «…»». */
  isMain: boolean;
  items: ShipmentItem[];
}

/** Group lines by `legSkladId` (the sklad each line posts under — the same key
 * the backend legs and the PDF group on), preserving first-seen order. */
function groupItemsBySklad(items: ShipmentItem[]): SkladGroup[] {
  const order: string[] = [];
  const byKey = new Map<string, ShipmentItem[]>();
  for (const item of items) {
    const key = item.legSkladId ?? item.skladId ?? '__none__';
    const group = byKey.get(key);
    if (group) group.push(item);
    else {
      byKey.set(key, [item]);
      order.push(key);
    }
  }
  return order.map((key) => {
    const groupItems = byKey.get(key)!;
    const named = groupItems.find((item) => item.skladName)?.skladName;
    return {
      key,
      name: named ?? '',
      isMain: !named && groupItems.some((item) => item.origin === 'MOYSKLAD'),
      items: groupItems,
    };
  });
}

/** Lines priced on this screen's «Моя цена» grid: MoySklad rows AND transferred-in
 * mirrors (store B sets its own sell price for goods moved in from store A; the
 * buy price stays A's cost). Owner-added MANUAL rows live in ExtraSkladItems. */
const isPriceable = (item: ShipmentItem): boolean =>
  item.origin === 'MOYSKLAD' || item.origin === 'MOVED_IN';

function draftFromShipment(shipment: Shipment): PriceDraft {
  const draft: PriceDraft = {};
  for (const item of shipment.items) {
    draft[item.id] = item.issuePriceKopeks == null ? '' : kopeksToRubString(item.issuePriceKopeks);
  }
  return draft;
}

/** Stable string of everything the owner can edit here (prices + other-sklad
 * items) — compared against a saved baseline for the unsaved-changes nav guard. */
function serializeDetail(draft: PriceDraft, extra: ExtraGroup[]): string {
  return JSON.stringify({
    draft: Object.fromEntries(Object.entries(draft).map(([k, v]) => [k, v ?? ''])),
    extra: extra.map((g) => ({
      skladId: g.skladId,
      rows: g.rows.map((r) => ({
        name: r.name ?? '',
        qty: r.qty ?? '',
        buy: r.buy ?? '',
        sell: r.sell ?? '',
      })),
    })),
  });
}

/** Valid drafts → kopeks; invalid/empty entries are simply absent. */
function draftToKopeks(draft: PriceDraft): Map<string, number> {
  const out = new Map<string, number>();
  for (const [itemId, value] of Object.entries(draft)) {
    if (!value || value === '-' || !isValidRubInput(value)) continue;
    const kopeks = rubToKopeks(value);
    if (kopeks >= 0) out.set(itemId, kopeks);
  }
  return out;
}

/**
 * /shipments/:id — the pricing screen (SPEC §1 integration): MoySklad columns
 * are read-only reference, «Моя цена» is what the store owes per unit.
 * «Провести» saves the prices and creates the PURCHASE+ISSUE pair.
 */
export function ShipmentDetailPage(): JSX.Element {
  const { t } = useTranslation();
  const sticky = useStickyTable();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  // Opened from a jadval cell: «Назад» and the post-«Провести» redirect go back
  // to that very table (month kept) instead of the shipments list.
  const returnTo = returnToOf(useLocation());
  const backTo = returnTo ?? '/shipments';
  const { user } = useAuth();
  // A SELLER only sets sale prices — no purchase price, no posting, no transfers.
  const isSeller = user?.role === 'SELLER';
  const { download: downloadPdf, isPending: pdfPending } = useDownloadShipmentPdf();
  const { id = '' } = useParams();
  const [draft, setDraft] = useState<PriceDraft>({});
  const [extraGroups, setExtraGroups] = useState<ExtraGroup[]>([]);
  const [markupPct, setMarkupPct] = useState<number | null>(null);
  const [moveState, setMoveState] = useState<{
    item: ShipmentItem;
    mode: 'transfer' | 'return';
  } | null>(null);
  const screens = Grid.useBreakpoint();
  // Phones: drop the buy line-SUM column so «Моя сумма» stays on screen while
  // typing prices; the buy total remains in the header and the summary row.
  const compact = !screens.sm;

  const query = useQuery({
    queryKey: queryKeys.shipment(id),
    queryFn: () => shipmentsApi.get(id),
    enabled: id !== '',
  });

  // Price memory for this shipment's store — remembered issue prices per
  // product from earlier накладные (buy comes from MoySklad here, so only sell).
  const storeId = query.data?.storeId;
  const lastPricesQuery = useQuery({
    queryKey: queryKeys.shipmentLastPrices(storeId ?? ''),
    queryFn: () => shipmentsApi.lastPrices(storeId!),
    // last-prices exposes buy prices → admin-only endpoint; sellers skip it.
    enabled: !!storeId && !isSeller,
    staleTime: 60_000,
  });

  // Unsaved-changes baseline (nav guard): serialized draft+extra as last seeded
  // from the server. `dirty` compares the live state against it, so the memory
  // auto-fill below is part of the baseline and never counts as a "change".
  const savedRef = useRef<string>('');
  const dirtyRef = useRef(false);
  const extraGroupsRef = useRef(extraGroups);
  extraGroupsRef.current = extraGroups;

  // Re-seed the drafts whenever the server copy changes (initial load, save,
  // transfer/return, webhook re-sync refetch) — the server is the source of
  // truth for SAVED prices. Remembered issue prices overlay still-unpriced lines
  // so recurring products auto-fill (only empty lines, never clobbering a saved
  // price). The seed is the nav-guard baseline; the LIVE draft is that seed with
  // the owner's unsaved edits carried over (mergeDraft), so a background refetch
  // — e.g. right after a transfer/return — never wipes inputs mid-typing.
  // Owner-added (other-sklad) items are seeded once per shipment for the same
  // reason. Both feed the baseline.
  const baselineRef = useRef<{ id: string; draft: PriceDraft } | null>(null);
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const extraSeededRef = useRef<string | null>(null);
  useEffect(() => {
    if (!query.data) return;
    const seededDraft = draftFromShipment(query.data);
    if (lastPricesQuery.data) {
      for (const item of query.data.items) {
        // Mirrors arrive priced (transfer requires it) — never overlay memory on them.
        if (item.issuePriceKopeks == null && item.origin !== 'MOVED_IN') {
          const mem = lastPricesQuery.data[normalizeName(item.name)];
          if (mem?.issuePriceKopeks != null) {
            seededDraft[item.id] = kopeksToRubString(mem.issuePriceKopeks);
          }
        }
      }
    }
    const prev = baselineRef.current;
    const liveDraft =
      prev && prev.id === query.data.id
        ? mergeDraft(seededDraft, prev.draft, draftRef.current)
        : seededDraft;
    baselineRef.current = { id: query.data.id, draft: seededDraft };
    setDraft(liveDraft);

    const seededExtra =
      extraSeededRef.current === query.data.id
        ? extraGroupsRef.current
        : extraGroupsFromItems(query.data.items);
    if (extraSeededRef.current !== query.data.id) {
      setExtraGroups(seededExtra);
      extraSeededRef.current = query.data.id;
    }
    savedRef.current = serializeDetail(seededDraft, seededExtra);
  }, [query.data, lastPricesQuery.data]);

  const onError = (error: Error): void => {
    message.error(apiErrorMessage(error) ?? t('common.requestError'));
  };

  const invalidateShipment = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['shipments'] });
    void queryClient.invalidateQueries({ queryKey: ['movements'] });
    // «Аналитика по товарам» reads draft lines too (status=all).
    void queryClient.invalidateQueries({ queryKey: ['reports', 'products'] });
  };

  // Non-fatal outcomes of a movement action (the MoySklad return-draft mirror).
  const showWarnings = (result: Shipment): void => {
    if (result.warnings?.includes('MS_RETURN_ALREADY_POSTED')) {
      message.warning(t('shipments.msReturnPostedWarn'), 8);
    }
    if (result.warnings?.includes('MS_RETURN_SYNC_FAILED')) {
      message.warning(t('shipments.msReturnFailedToast'), 8);
    }
  };

  // The grid lines (MoySklad + transferred-in) that have a valid «Моя цена» entered.
  const collectPriced = (): { itemId: string; issuePriceKopeks: number }[] => {
    const priced = draftToKopeks(draft);
    return (query.data?.items ?? [])
      .filter((item) => isPriceable(item) && priced.has(item.id))
      .map((item) => ({ itemId: item.id, issuePriceKopeks: priced.get(item.id)! }));
  };

  // Before a transfer/return: persist the grid «Моя цена» inputs that differ
  // from the server copy. The move refetches the накладная, and a transfer needs
  // the line priced server-side (ITEM_NOT_PRICED otherwise) — so what the owner
  // typed is committed first instead of being lost. Pending docs only (a posted
  // one has no inputs; savePrices rejects it anyway).
  const savePricesBeforeMove = async (): Promise<void> => {
    if (!query.data || query.data.status !== 'PENDING_PRICING') return;
    const changed = collectPriced().filter(({ itemId, issuePriceKopeks }) => {
      const item = query.data!.items.find((i) => i.id === itemId);
      return item != null && item.issuePriceKopeks !== issuePriceKopeks;
    });
    if (changed.length > 0) await shipmentsApi.savePrices(id, { items: changed });
  };

  // Persist the grid issue prices AND the owner-added other-sklad items together.
  const saveAll = async (): Promise<void> => {
    const gridPriced = collectPriced();
    if (gridPriced.length > 0) await shipmentsApi.savePrices(id, { items: gridPriced });
    // Owner-added items (buy+sell) are an admin concern — sellers only price.
    if (!isSeller)
      await shipmentsApi.saveExtraItems(id, { items: extraGroupsToItems(extraGroups) });
  };

  const saveMutation = useMutation({
    mutationFn: saveAll,
    onSuccess: () => {
      message.success(t('common.saved'));
      invalidateShipment();
    },
    onError,
  });

  const postMutation = useMutation({
    mutationFn: async () => {
      await saveAll();
      return shipmentsApi.post(id);
    },
    onSuccess: (result) => {
      const sklads = result.legs
        .map((leg) =>
          t('quick.skladDebtPart', {
            sklad: leg.purchase.sklad?.name ?? '',
            skladDebt: formatKopeks(leg.skladDebt),
          }),
        )
        .join('; ');
      message.success(
        t('quick.combinedSuccessMulti', {
          sklads,
          store: result.legs[0]?.issue?.store?.name ?? query.data?.storeName ?? '',
          storeBalance: formatKopeks(result.storeBalance),
        }),
        8,
      );
      invalidateMoneyData(queryClient);
      invalidateShipment();
      // Re-seed the extra section from the posted server truth on refetch.
      extraSeededRef.current = null;
      if (returnTo) {
        // Posted from a jadval: the prices are saved, so the guard has nothing
        // left to protect — straight back to the table that sent us here.
        dirtyRef.current = false;
        navigate(returnTo, { replace: true });
      }
    },
    onError,
  });

  const undoMutation = useMutation({
    mutationFn: (movementId: string) => shipmentsApi.undoMovement(id, movementId),
    onSuccess: (result) => {
      message.success(t('common.saved'));
      showWarnings(result);
      invalidateMoneyData(queryClient);
      invalidateShipment();
    },
    onError,
  });

  // «Повторить»: re-run the MoySklad return-draft mirror after a failure.
  const syncMsReturnMutation = useMutation({
    mutationFn: () => shipmentsApi.syncMsReturn(id),
    onSuccess: (result) => {
      if (result.warnings?.length) showWarnings(result);
      else message.success(t('shipments.msReturnRetryDone'));
      invalidateShipment();
    },
    onError,
  });

  // «Изменить»: revert a POSTED накладная to PENDING_PRICING so the same pending
  // screen re-opens (editor / pricing grid) and it can be changed + re-posted.
  const unpostMutation = useMutation({
    mutationFn: () => shipmentsApi.unpost(id),
    onSuccess: () => {
      message.success(t('shipments.unpostDone'));
      invalidateMoneyData(queryClient);
      invalidateShipment();
      // Re-seed the pricing grid + extra section from the (now PENDING) server truth.
      extraSeededRef.current = null;
      baselineRef.current = null;
    },
    onError,
  });

  // «Удалить»: remove the накладная AND (for a posted one) the ledger operations
  // it created. Shown for manual documents and for a MoySklad document whose
  // отгрузка was deleted in MoySklad (it will never come back via sync).
  const deleteMutation = useMutation({
    mutationFn: () => shipmentsApi.remove(id),
    onSuccess: () => {
      message.success(t('shipments.deleteDone'));
      dirtyRef.current = false; // our own redirect — don't trip the nav guard
      invalidateMoneyData(queryClient);
      invalidateShipment();
      navigate(backTo, { replace: true });
    },
    onError,
  });

  if (query.isLoading) return <LoadingBlock />;
  if (query.isError || !query.data) return <ErrorBlock onRetry={() => void query.refetch()} />;

  const shipment = query.data;
  const pending = shipment.status === 'PENDING_PRICING';
  const manual = shipment.source === 'MANUAL';
  // Editing/deleting is unavailable while a transfer/return references this doc
  // (a mirror line depends on its msPositionId — undo it first).
  const hasMovements = shipment.items.some(
    (item) => item.movements.length > 0 || item.origin === 'MOVED_IN',
  );
  // «Изменить» is additionally unavailable when the MoySklad demand was deleted —
  // such a doc is a dead mirror: the only sensible action left is «Удалить».
  const deletedInMs = !!shipment.msDeletedAt;
  const unpostBlocked = deletedInMs || hasMovements;
  const deletable = manual || deletedInMs;

  // A pending manual накладная is fully editable — the editor owns it (and mounts
  // its OWN nav guard). Returning here keeps this screen's guard unmounted, so only
  // one blocker is ever active (react-router allows a single blocker).
  if (manual && pending) return <ManualShipmentEditor key={shipment.id} shipment={shipment} />;

  // Unsaved-changes flag for this screen's nav guard (draft prices + extra items).
  dirtyRef.current = serializeDetail(draft, extraGroups) !== savedRef.current;

  // MoySklad-origin lines + transferred-in mirrors drive the issue-price grid;
  // owner-added lines (from other sklads) live in the extra section below and
  // post as their own legs.
  const msItems = shipment.items.filter((item) => item.origin === 'MOYSKLAD');
  const movedInItems = shipment.items.filter((item) => item.origin === 'MOVED_IN');
  const priceableItems = shipment.items.filter(isPriceable);
  // Pending: MoySklad rows + transferred-in mirrors (both priced here);
  // owner-added MANUAL rows live in the ExtraSkladItems section. Posted: all.
  const tableItems = pending
    ? shipment.items.filter((item) => item.origin !== 'MANUAL')
    : shipment.items;
  const priced = draftToKopeks(draft);
  // Effective «Моя цена» for a line: the live draft if present, else the saved
  // price. A still-active line at exactly 0 whose goods cost something is a
  // «Бесплатно» (free) line — its buy cost posts as a Расход.
  const effIssue = (item: ShipmentItem): number | null =>
    priced.has(item.id) ? priced.get(item.id)! : item.issuePriceKopeks;
  const isFree = (item: ShipmentItem): boolean =>
    item.activeQuantityMilli > 0 && effIssue(item) === 0 && (item.buyPriceKopeks ?? 0) > 0;
  // Only still-active grid lines must be priced to post.
  const allPriced = priceableItems.every(
    (item) => item.activeQuantityMilli === 0 || priced.has(item.id),
  );
  const draftIssueTotal = (items: ShipmentItem[]): number =>
    items.reduce(
      (sum, item) =>
        sum + (priced.has(item.id) ? lineSum(item.activeQuantityMilli, priced.get(item.id)!) : 0),
      0,
    );
  const msIssueTotal = draftIssueTotal(msItems);
  // MoySklad purchase drops by the buy value of quantities that left the doc.
  const movedMsBuy = msItems.reduce(
    (sum, item) => sum + lineSum(item.movedOutMilli, item.buyPriceKopeks ?? 0),
    0,
  );
  // Transferred-in mirrors post as their own sklad leg (like MANUAL rows):
  // purchase = active qty × A's buy, issue = active qty × B's «Моя цена».
  // Sellers get buy = null → 0, consistent with msSum being null for them.
  const movedInBuy = movedInItems.reduce(
    (sum, item) => sum + lineSum(item.activeQuantityMilli, item.buyPriceKopeks ?? 0),
    0,
  );
  const movedInSell = draftIssueTotal(movedInItems);
  const extraBuy = extraSideTotal(extraGroups, 'buy');
  const extraSell = extraSideTotal(extraGroups, 'sell');
  const extraComplete = extraGroupsComplete(extraGroups);
  // msSum is null for sellers (purchase hidden) — they never see purchaseTotal.
  const purchaseTotal = (shipment.msSum ?? 0) - movedMsBuy + extraBuy + movedInBuy;
  const issueTotal = msIssueTotal + extraSell + movedInSell;
  const markup = issueTotal - purchaseTotal;
  // Postable once everything is priced and there is something to post — a sell
  // (issue > 0) OR a pure giveaway (purchase > 0, all lines free): the backend
  // still records the sklad debt and books the free buy cost as a Расход.
  const canPost = allPriced && extraComplete && (issueTotal > 0 || purchaseTotal > 0);
  const canSave = collectPriced().length > 0 || (!isSeller && extraGroups.length > 0);
  // Lines whose sklad buy price moved since this store's previous накладная.
  const buyChangedCount = priceableItems.filter((item) =>
    priceChanged(item.prevBuy?.priceKopeks, item.buyPriceKopeks),
  ).length;

  // «Рассчитать»: fill every grid «Моя цена» = buy price + markup %. Purely
  // fills the drafts — the owner still reviews/edits each row and «Провести».
  const applyMarkup = (): void => {
    if (markupPct == null) {
      message.warning(t('shipments.markupNeedPct'));
      return;
    }
    setDraft((prev) => {
      const next = { ...prev };
      for (const item of priceableItems) {
        if (item.buyPriceKopeks == null) continue;
        // Integer kopeks in, integer kopeks out — round once, per unit.
        const issue = Math.round((item.buyPriceKopeks * (100 + markupPct)) / 100);
        next[item.id] = kopeksToRubString(issue);
      }
      return next;
    });
    message.success(t('shipments.markupApplied', { pct: markupPct }));
  };

  const columns = [
    {
      title: '№',
      key: 'idx',
      width: 44,
      align: 'right' as const,
      render: (_: unknown, _item: ShipmentItem, index: number) => (
        <Typography.Text type="secondary">{index + 1}</Typography.Text>
      ),
    },
    {
      title: t('shipments.colItem'),
      key: 'name',
      render: (_: unknown, item: ShipmentItem) => (
        <div style={{ display: 'grid', gap: 2 }}>
          <Typography.Text strong>{item.name}</Typography.Text>
          {item.origin === 'MOVED_IN' && item.movedInFromStoreName ? (
            <Tag color="blue" style={{ width: 'fit-content', margin: 0 }}>
              {t('shipments.movedInTag', { store: item.movedInFromStoreName })}
            </Tag>
          ) : null}
          {item.movements.map((mv) => (
            <Tag
              key={mv.id}
              color={mv.kind === 'RETURN' ? 'orange' : 'purple'}
              style={{ width: 'fit-content', margin: 0 }}
            >
              {mv.kind === 'RETURN'
                ? t('shipments.returnedTag', { qty: formatQuantityMilli(mv.quantityMilli) }) +
                  (mv.msReturnName
                    ? t('shipments.msReturnTagSuffix', { name: mv.msReturnName })
                    : '')
                : t('shipments.movedOutTag', {
                    qty: formatQuantityMilli(mv.quantityMilli),
                    store: mv.toStoreName ?? '',
                  })}
            </Tag>
          ))}
        </div>
      ),
    },
    {
      title: t('shipments.colQty'),
      key: 'qty',
      align: 'right' as const,
      render: (_: unknown, item: ShipmentItem) =>
        item.movedOutMilli > 0 ? (
          <span>
            <Typography.Text delete type="secondary" style={{ fontSize: 12 }}>
              {formatQuantityMilli(item.quantityMilli)}
            </Typography.Text>{' '}
            <Typography.Text strong>
              {formatQuantityMilli(item.activeQuantityMilli)}
            </Typography.Text>
          </span>
        ) : (
          formatQuantityMilli(item.quantityMilli)
        ),
    },
    {
      title: t(manual ? 'shipments.buyPriceLabel' : 'shipments.colMsPrice'),
      dataIndex: 'buyPriceKopeks',
      key: 'buyPrice',
      align: 'right' as const,
      // «было X · +12,5 %» when the sklad price moved since this store's previous
      // накладная — so the owner re-thinks «Моя цена» instead of trusting memory.
      render: (v: number | null, item: ShipmentItem) => {
        const prev = item.prevBuy;
        if (!priceChanged(prev?.priceKopeks, v)) return v == null ? '—' : formatKopeks(v);
        return (
          <div style={{ display: 'grid', gap: 2, justifyItems: 'end' }}>
            <span>{formatKopeks(v!)}</span>
            <Tooltip
              title={t('shipments.prevDocTooltip', {
                name: prev!.msName,
                date: dayjs(prev!.msDate).format('DD.MM.YYYY'),
              })}
            >
              <Tag
                color={v! > prev!.priceKopeks ? 'red' : 'green'}
                style={{ margin: 0, fontSize: 11 }}
              >
                {t('shipments.prevBuyTag', { price: formatKopeks(prev!.priceKopeks) })}
                {formatDeltaPct(prev!.priceKopeks, v!)
                  ? ` · ${formatDeltaPct(prev!.priceKopeks, v!)}`
                  : ''}
              </Tag>
            </Tooltip>
          </div>
        );
      },
    },
    ...(compact
      ? []
      : [
          {
            title: t(manual ? 'shipments.buyTotal' : 'shipments.colMsSum'),
            dataIndex: 'buySumKopeks' as const,
            key: 'buySum',
            align: 'right' as const,
            render: (v: number | null) => (v == null ? '—' : formatKopeks(v)),
          },
        ]),
    {
      title: t('shipments.colMyPrice'),
      key: 'myPrice',
      width: compact ? 120 : 150,
      render: (_: unknown, item: ShipmentItem) => {
        // MoySklad lines AND transferred-in mirrors are priced here (store B's
        // own sell price); owner-added MANUAL rows are priced in ExtraSkladItems.
        const field =
          pending && isPriceable(item) ? (
            <AmountInput
              size="middle"
              value={draft[item.id] ?? ''}
              onChange={(value) => setDraft((prev) => ({ ...prev, [item.id]: value }))}
            />
          ) : item.issuePriceKopeks == null ? (
            '—'
          ) : (
            formatKopeks(item.issuePriceKopeks)
          );
        // «пред. цена» — the owner's price on this store's previous накладная;
        // coloured when the current price differs (what a seller compares against).
        const prev = item.prevIssue;
        if (!prev) return field;
        const current = effIssue(item);
        const changed = priceChanged(prev.priceKopeks, current);
        const delta = changed ? formatDeltaPct(prev.priceKopeks, current!) : null;
        return (
          <div style={{ display: 'grid', gap: 2 }}>
            {field}
            <Tooltip
              title={t('shipments.prevDocTooltip', {
                name: prev.msName,
                date: dayjs(prev.msDate).format('DD.MM.YYYY'),
              })}
            >
              {/* Wraps at « · » (amounts use non-breaking spaces): the column is
                  120–150px wide with table-layout: fixed on phones, so a nowrap
                  line ran under the next cell and was painted over. */}
              <Typography.Text
                type={changed ? (current! > prev.priceKopeks ? 'danger' : 'success') : 'secondary'}
                style={{ fontSize: 11, lineHeight: 1.3 }}
              >
                {t('shipments.prevIssueLine', { price: formatKopeks(prev.priceKopeks) })}
                {delta ? ` · ${delta}` : ''}
              </Typography.Text>
            </Tooltip>
          </div>
        );
      },
    },
    {
      title: t('shipments.colMySum'),
      key: 'mySum',
      align: 'right' as const,
      render: (_: unknown, item: ShipmentItem) => {
        if (isFree(item)) {
          return (
            <Tag color="orange" style={{ margin: 0 }}>
              {t('shipments.freeTag')}
            </Tag>
          );
        }
        const price = effIssue(item);
        return price != null && item.activeQuantityMilli > 0 ? (
          <Typography.Text strong>
            {formatKopeks(lineSum(item.activeQuantityMilli, price))}
          </Typography.Text>
        ) : (
          '—'
        );
      },
    },
    {
      title: '',
      key: 'actions',
      width: compact ? 96 : 116,
      render: (_: unknown, item: ShipmentItem) => (
        <Space size={4} wrap>
          {item.activeQuantityMilli > 0 ? (
            <>
              <Button
                size="small"
                icon={<SwapOutlined />}
                title={t('shipments.transferBtn')}
                onClick={() => setMoveState({ item, mode: 'transfer' })}
              />
              <Button
                size="small"
                icon={<RollbackOutlined />}
                title={t('shipments.returnBtn')}
                onClick={() => setMoveState({ item, mode: 'return' })}
              />
            </>
          ) : null}
          {item.movements.map((mv) => (
            <Button
              key={mv.id}
              size="small"
              type="link"
              icon={<UndoOutlined />}
              loading={undoMutation.isPending}
              onClick={() => undoMutation.mutate(mv.id)}
            >
              {t('shipments.undo')}
            </Button>
          ))}
        </Space>
      ),
    },
  ];

  // A SELLER never sees the purchase price (buy columns) and cannot move items.
  const visibleColumns = isSeller
    ? columns.filter((c) => !['buyPrice', 'buySum', 'actions'].includes(c.key as string))
    : columns;

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <UnsavedChangesGuard shouldBlock={() => dirtyRef.current} />
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(backTo)}>
          {t('common.back')}
        </Button>
        <Typography.Title level={5} style={{ margin: 0 }}>
          {t('shipments.rowTitle', { name: shipment.msName })}
        </Typography.Title>
        <ShipmentStatusTag status={shipment.status} />
        <ActorLine
          createdBy={shipment.createdByName}
          postedBy={shipment.postedByName}
          style={{ display: 'inline', fontSize: 13 }}
        />
        {manual ? <Tag>{t('shipments.manualTag')}</Tag> : null}
        {shipment.msChangedAfterPost ? (
          <Tag color="orange">{t('shipments.changedInMs')}</Tag>
        ) : null}
        {shipment.msDeletedAt ? <Tag color="red">{t('shipments.deletedInMs')}</Tag> : null}
        {shipment.msReturn?.id ? (
          <Tag color="green">
            {shipment.msReturn.href ? (
              <a href={shipment.msReturn.href} target="_blank" rel="noreferrer">
                {t('shipments.msReturnDraft', { name: shipment.msReturn.name ?? '' })}
              </a>
            ) : (
              t('shipments.msReturnDraft', { name: shipment.msReturn.name ?? '' })
            )}
          </Tag>
        ) : null}
        {shipment.msReturn?.syncError && !isSeller ? (
          <Space size={4}>
            <Tooltip title={shipment.msReturn.syncError}>
              <Tag color="red" style={{ margin: 0 }}>
                {t('shipments.msReturnFailed')}
              </Tag>
            </Tooltip>
            <Button
              size="small"
              icon={<ReloadOutlined />}
              loading={syncMsReturnMutation.isPending}
              onClick={() => syncMsReturnMutation.mutate()}
            >
              {t('shipments.msReturnRetry')}
            </Button>
          </Space>
        ) : null}
        <div style={{ marginInlineStart: 'auto' }}>
          {isSeller ? (
            <Button
              icon={<DownloadOutlined />}
              loading={pdfPending}
              onClick={() => downloadPdf({ id, view: 'store', msName: shipment.msName })}
            >
              {t('shipments.downloadPdf')}
            </Button>
          ) : (
            <Dropdown
              menu={{
                items: [
                  { key: 'store', label: t('shipments.pdfStore') },
                  { key: 'sklad', label: t('shipments.pdfSklad') },
                ],
                onClick: ({ key }) =>
                  void downloadPdf({ id, view: key as 'store' | 'sklad', msName: shipment.msName }),
              }}
            >
              <Button icon={<DownloadOutlined />} loading={pdfPending}>
                {t('shipments.downloadPdf')}
              </Button>
            </Dropdown>
          )}
        </div>
      </div>

      <Card size="small">
        <Descriptions
          size="small"
          column={{ xs: 1, sm: 2 }}
          // Phones/small tablets: label above value. Side-by-side, the long
          // «Сумма по МойСклад (закупка)» label left the no-wrap amount a cell too
          // narrow for it (antd lays the grid out as a fixed table), so the number
          // spilled past the card and made the whole page scroll sideways.
          layout={screens.md ? 'horizontal' : 'vertical'}
          items={[
            {
              key: 'date',
              label: t('common.date'),
              children: dayjs(shipment.msDate).format('D MMMM YYYY'),
            },
            { key: 'store', label: t('common.store'), children: shipment.storeName },
            // Purchase total is hidden from sellers.
            ...(isSeller || shipment.msSum == null
              ? []
              : [
                  {
                    key: 'msSum',
                    label: t(manual ? 'shipments.buyTotal' : 'shipments.msSumLabel'),
                    children: (
                      <Typography.Text strong style={{ whiteSpace: 'nowrap' }}>
                        {formatKopeks(shipment.msSum)}
                      </Typography.Text>
                    ),
                  },
                ]),
            manual
              ? {
                  key: 'sklad',
                  label: t('common.sklad'),
                  children: shipment.skladName ?? '—',
                }
              : {
                  key: 'agent',
                  label: t('shipments.agentLabel'),
                  children: shipment.msAgentName ?? '—',
                },
          ]}
        />
      </Card>

      {pending && !isSeller && buyChangedCount > 0 ? (
        <Alert
          type="warning"
          showIcon
          message={t('shipments.buyChangedAlert', { count: buyChangedCount })}
        />
      ) : null}

      {pending && !isSeller ? (
        <Card size="small">
          <Space direction="vertical" size={4} style={{ width: '100%' }}>
            <Space wrap align="center">
              <Typography.Text strong>{t('shipments.markupPctLabel')}</Typography.Text>
              <InputNumber
                value={markupPct}
                onChange={setMarkupPct}
                onPressEnter={applyMarkup}
                min={0}
                max={100000}
                step={1}
                addonAfter="%"
                placeholder={t('shipments.markupPlaceholder')}
                style={{ width: 140 }}
              />
              <Button
                icon={<CalculatorOutlined />}
                onClick={applyMarkup}
                disabled={markupPct == null}
              >
                {t('shipments.markupCalc')}
              </Button>
            </Space>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {t('shipments.markupHint')}
            </Typography.Text>
          </Space>
        </Card>
      ) : null}

      {(() => {
        const skladGroups = groupItemsBySklad(tableItems);
        // A POSTED multi-sklad накладная is split into one section per sklad —
        // the way it was created. The pending screen keeps its single pricing
        // grid + summary (owner-added extras are already in their own cards
        // below), so grouping applies only to the read-only posted view.
        const splitBySklad = !pending && skladGroups.length > 1;
        if (!splitBySklad) {
          return (
            <Card size="small" styles={{ body: { padding: 0 } }}>
              <Table<ShipmentItem>
                dataSource={tableItems}
                columns={visibleColumns}
                rowKey="id"
                size="small"
                pagination={false}
                sticky={sticky}
                // A line with «Моя цена» set is tinted green so still-unpriced rows
                // stand out; a free (0-price) line is tinted amber to read as "given
                // away", not "unpriced".
                rowClassName={(item) => {
                  if (!pending) return '';
                  if (isFree(item)) return 'row-free';
                  return priced.has(item.id) || item.issuePriceKopeks != null ? 'row-priced' : '';
                }}
                scroll={{ x: compact ? 480 : 640 }}
                summary={
                  isSeller
                    ? undefined
                    : () => (
                        <Table.Summary.Row>
                          {/* Compact drops the buySum column — msSum then sits under the
                              per-unit buy-price column instead. The leading colSpan also
                              covers the № column. */}
                          <Table.Summary.Cell index={0} colSpan={3}>
                            <Typography.Text strong>{t('shipments.totalRow')}</Typography.Text>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={1} align="right" colSpan={compact ? 1 : 2}>
                            <Typography.Text strong>
                              {formatKopeks(shipment.msSum ?? 0)}
                            </Typography.Text>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={2} />
                          <Table.Summary.Cell index={3} align="right">
                            <Typography.Text strong>
                              {msIssueTotal > 0 ? formatKopeks(msIssueTotal) : '—'}
                            </Typography.Text>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={4} />
                        </Table.Summary.Row>
                      )
                }
              />
            </Card>
          );
        }
        return (
          <div style={{ display: 'grid', gap: 12 }}>
            {skladGroups.map((group) => {
              // Subtotals over each line's ACTIVE quantity (so transferred-away
              // amounts don't count) — matching the group total on the editor.
              const groupSell = group.items.reduce(
                (sum, item) =>
                  item.issuePriceKopeks != null && item.activeQuantityMilli > 0
                    ? sum + lineSum(item.activeQuantityMilli, item.issuePriceKopeks)
                    : sum,
                0,
              );
              const groupBuy = group.items.reduce(
                (sum, item) =>
                  item.buyPriceKopeks != null && item.activeQuantityMilli > 0
                    ? sum + lineSum(item.activeQuantityMilli, item.buyPriceKopeks)
                    : sum,
                0,
              );
              return (
                <Card
                  key={group.key}
                  size="small"
                  title={
                    group.isMain
                      ? t('shipments.moyskladGroup')
                      : t('shipments.extraGroupTitle', { sklad: group.name })
                  }
                  styles={{ body: { padding: 0 } }}
                >
                  <Table<ShipmentItem>
                    dataSource={group.items}
                    columns={visibleColumns}
                    rowKey="id"
                    size="small"
                    pagination={false}
                    scroll={{ x: compact ? 480 : 640 }}
                  />
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      gap: 16,
                      padding: '8px 12px',
                      borderTop: '1px solid #f0f0f0',
                    }}
                  >
                    {!isSeller ? (
                      <Typography.Text type="secondary">
                        {t('shipments.buyShort')}:{' '}
                        <Typography.Text strong>{formatKopeks(groupBuy)}</Typography.Text>
                      </Typography.Text>
                    ) : null}
                    <Typography.Text type="secondary">
                      {t('shipments.colMySum')}:{' '}
                      <Typography.Text strong>{formatKopeks(groupSell)}</Typography.Text>
                    </Typography.Text>
                  </div>
                </Card>
              );
            })}
          </div>
        );
      })()}

      {pending && !isSeller ? (
        <Card size="small" title={t('shipments.extraItemsTitle')}>
          <ExtraSkladItems value={extraGroups} onChange={setExtraGroups} storeId={storeId} />
        </Card>
      ) : null}

      {issueTotal > 0 && !isSeller ? (
        <Card size="small">
          <div style={{ display: 'grid', gap: 4 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography.Text type="secondary">{t('shipments.msSumLabel')}</Typography.Text>
              <Typography.Text strong>{formatKopeks(purchaseTotal)}</Typography.Text>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography.Text type="secondary">{t('shipments.sellTotal')}</Typography.Text>
              <Typography.Text strong>{formatKopeks(issueTotal)}</Typography.Text>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography.Text>{t('shipments.markupLine')}</Typography.Text>
              <Typography.Text strong style={{ color: markup >= 0 ? OWED_TO_OWNER : OWNER_OWES }}>
                {formatKopeks(markup)}
              </Typography.Text>
            </div>
          </div>
        </Card>
      ) : null}

      {pending && isSeller ? (
        // A seller saves sale prices only — posting (проведение) is an admin action.
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            size="large"
            onClick={() => saveMutation.mutate()}
            disabled={!canSave}
            loading={saveMutation.isPending}
          >
            {t('shipments.savePrices')}
          </Button>
          <Typography.Text type="secondary">{t('seller.pricingHint')}</Typography.Text>
        </div>
      ) : pending ? (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Button
            icon={<SaveOutlined />}
            size="large"
            onClick={() => saveMutation.mutate()}
            disabled={!canSave}
            loading={saveMutation.isPending}
          >
            {t('shipments.savePrices')}
          </Button>
          <Popconfirm
            title={t('shipments.postConfirmTitle')}
            description={t('shipments.postConfirmText', {
              purchase: formatKopeks(purchaseTotal),
              issue: formatKopeks(issueTotal),
            })}
            okText={t('shipments.postButton')}
            cancelText={t('common.cancel')}
            onConfirm={() => postMutation.mutate()}
            disabled={!canPost}
          >
            <Button
              type="primary"
              icon={<CheckOutlined />}
              size="large"
              disabled={!canPost}
              loading={postMutation.isPending}
            >
              {t('shipments.postButton')}
            </Button>
          </Popconfirm>
          {!canPost ? (
            <Typography.Text type="secondary" style={{ alignSelf: 'center' }}>
              {t('shipments.priceAllHint')}
            </Typography.Text>
          ) : null}
        </div>
      ) : (
        <Card size="small">
          <div style={{ display: 'grid', gap: 12 }}>
            <Typography.Text type="secondary">
              {t('shipments.postedInfo', {
                date: shipment.postedAt ? dayjs(shipment.postedAt).format('D MMMM YYYY HH:mm') : '',
                issue: shipment.issueTotal != null ? formatKopeks(shipment.issueTotal) : '',
              })}
            </Typography.Text>
            {/* «Изменить»: revert to PENDING so the same editing screen re-opens.
                Blocked while transfers/returns reference this doc (undo them first)
                or its MoySklad demand was deleted — mirror the backend guards so we
                never offer an action that would 409. Sellers see the posted doc
                read-only (unpost/delete are admin-only). */}
            <div
              style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}
              hidden={isSeller}
            >
              <Popconfirm
                title={t('shipments.unpostConfirmTitle')}
                description={t('shipments.unpostConfirmText')}
                okText={t('shipments.unpostButton')}
                cancelText={t('common.cancel')}
                onConfirm={() => unpostMutation.mutate()}
                disabled={unpostBlocked}
              >
                <Button
                  icon={<EditOutlined />}
                  disabled={unpostBlocked}
                  loading={unpostMutation.isPending}
                >
                  {t('shipments.unpostButton')}
                </Button>
              </Popconfirm>
              {deletable ? (
                <Popconfirm
                  title={t('shipments.deleteConfirmTitle')}
                  description={t(
                    deletedInMs ? 'shipments.deleteMsConfirmText' : 'shipments.deleteConfirmText',
                  )}
                  okText={t('shipments.deleteButton')}
                  okButtonProps={{ danger: true }}
                  cancelText={t('common.cancel')}
                  onConfirm={() => deleteMutation.mutate()}
                  disabled={hasMovements}
                >
                  <Button
                    danger
                    icon={<DeleteOutlined />}
                    disabled={hasMovements}
                    loading={deleteMutation.isPending}
                  >
                    {t('shipments.deleteButton')}
                  </Button>
                </Popconfirm>
              ) : null}
              {unpostBlocked ? (
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {t(deletedInMs ? 'shipments.deletedInMsHint' : 'shipments.unpostMovementsHint')}
                </Typography.Text>
              ) : null}
            </div>
          </div>
        </Card>
      )}

      <MoveItemModal
        shipment={shipment}
        item={moveState?.item ?? null}
        mode={moveState?.mode ?? 'transfer'}
        onBeforeMove={savePricesBeforeMove}
        onClose={() => setMoveState(null)}
      />
    </div>
  );
}
